import React, { useEffect, useMemo, useState } from 'react';
import api from '../lib/api';
import { toast } from 'sonner';
import {
  Plus, Edit2, Trash2, ChevronDown, ChevronRight, FileText, DollarSign,
  TrendingUp, TrendingDown, Building, RefreshCw, ShieldCheck, GitBranch,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { useAuth } from '../context/AuthContext';

const accountTypeIcons = {
  asset: TrendingUp,
  liability: TrendingDown,
  equity: Building,
  income: DollarSign,
  expense: FileText,
};

const accountTypeColors = {
  asset: 'text-blue-700 bg-blue-50',
  liability: 'text-red-700 bg-red-50',
  equity: 'text-purple-700 bg-purple-50',
  income: 'text-emerald-700 bg-emerald-50',
  expense: 'text-orange-700 bg-orange-50',
};

const categories = {
  asset: [
    ['current_asset', 'Current Asset'], ['fixed_asset', 'Fixed Asset'], ['contra_asset', 'Contra Asset'],
    ['bank', 'Bank'], ['cash', 'Cash'], ['accounts_receivable', 'Accounts Receivable'],
    ['inventory', 'Inventory'], ['other_asset', 'Other Asset'],
  ],
  liability: [
    ['current_liability', 'Current Liability'], ['long_term_liability', 'Long-term Liability'],
    ['accounts_payable', 'Accounts Payable'], ['other_liability', 'Other Liability'],
  ],
  equity: [['capital', 'Capital'], ['retained_earnings', 'Retained Earnings'], ['reserve', 'Reserve']],
  income: [['revenue', 'Revenue'], ['other_income', 'Other Income']],
  expense: [
    ['cost_of_goods_sold', 'Cost of Goods Sold'], ['administrative_expense', 'Administrative Expense'],
    ['selling_marketing_expense', 'Selling & Marketing Expense'], ['distribution_expense', 'Distribution Expense'],
    ['finance_expense', 'Finance Expense'], ['tax_expense', 'Tax Expense'],
    ['other_expense', 'Other Expense'], ['operating_expense', 'Legacy Operating Expense'],
  ],
};

const emptyForm = {
  code: '', name: '', account_type: 'asset', category: 'current_asset', description: '',
  opening_balance: 0, parent_account_id: '', report_group: '', normal_balance: 'debit',
  is_control_account: false, manual_posting_allowed: true, cash_flow_category: '',
  manual_code_override: false,
};

const titleCase = (value) => String(value || '').replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

export default function ChartOfAccounts() {
  const { user } = useAuth();
  const isAdmin = String(user?.role || '').toLowerCase() === 'admin';
  const [accounts, setAccounts] = useState([]);
  const [parents, setParents] = useState([]);
  const [reportGroups, setReportGroups] = useState({});
  const [loading, setLoading] = useState(true);
  const [expandedTypes, setExpandedTypes] = useState({ asset: true, liability: true, equity: true, income: true, expense: true });
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingAccount, setEditingAccount] = useState(null);
  const [formData, setFormData] = useState(emptyForm);
  const [nextCode, setNextCode] = useState('');
  const [accountSuggestions, setAccountSuggestions] = useState([]);

  useEffect(() => { fetchPage(); }, []);

  useEffect(() => {
    if (!editingAccount && isModalOpen && !formData.manual_code_override) {
      fetchNextCode(formData.account_type, formData.parent_account_id);
    }
  }, [formData.account_type, formData.parent_account_id, formData.manual_code_override, editingAccount, isModalOpen]);

  useEffect(() => {
    if (!isModalOpen || editingAccount || formData.name.trim().length < 3) {
      setAccountSuggestions([]);
      return undefined;
    }
    const timer = window.setTimeout(async () => {
      try {
        const response = await api.get('/finance/chart-of-accounts/suggestions/existing', {
          params: { name: formData.name.trim(), account_type: formData.account_type },
        });
        setAccountSuggestions(response.data?.items || []);
      } catch (error) {
        setAccountSuggestions([]);
      }
    }, 350);
    return () => window.clearTimeout(timer);
  }, [formData.name, formData.account_type, isModalOpen, editingAccount]);

  const fetchPage = async () => {
    try {
      setLoading(true);
      const [accountRes, taxonomyRes] = await Promise.all([
        api.get('/finance/chart-of-accounts'),
        api.get('/finance/chart-of-accounts/taxonomy/enterprise'),
      ]);
      setAccounts(accountRes.data || []);
      setParents(taxonomyRes.data?.parents || []);
      setReportGroups(taxonomyRes.data?.report_groups || {});
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to load Chart of Accounts');
    } finally {
      setLoading(false);
    }
  };

  const fetchNextCode = async (accountType, parentId) => {
    try {
      const response = await api.get(`/finance/chart-of-accounts/next-code/${accountType}`, {
        params: parentId ? { parent_account_id: parentId } : {},
      });
      setNextCode(response.data.next_code);
      setFormData((prev) => ({ ...prev, code: response.data.next_code }));
    } catch (error) {
      setNextCode('');
      toast.error(error.response?.data?.detail || 'Could not generate an account code');
    }
  };

  const initializeAccounts = async () => {
    try {
      const response = await api.post('/finance/chart-of-accounts/initialize');
      toast.success(response.data.added > 0 ? `Added ${response.data.added} missing accounts` : 'All default accounts already exist');
      fetchPage();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to initialize accounts');
    }
  };

  const resetForm = () => {
    setFormData(emptyForm);
    setEditingAccount(null);
    setNextCode('');
    setAccountSuggestions([]);
  };

  const openAddModal = () => {
    resetForm();
    setIsModalOpen(true);
  };

  const openEditModal = (account) => {
    setEditingAccount(account);
    setFormData({
      ...emptyForm,
      code: account.code,
      name: account.name,
      account_type: account.account_type,
      category: account.category || categories[account.account_type]?.[0]?.[0],
      description: account.description || '',
      parent_account_id: account.parent_account_id || '',
      report_group: account.report_group || '',
      normal_balance: account.normal_balance || (['asset', 'expense'].includes(account.account_type) ? 'debit' : 'credit'),
      is_control_account: Boolean(account.is_control_account),
      manual_posting_allowed: account.manual_posting_allowed !== false,
      cash_flow_category: account.cash_flow_category || '',
    });
    setIsModalOpen(true);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    try {
      if (editingAccount) {
        await api.put(`/finance/chart-of-accounts/${editingAccount.id}`, {
          name: formData.name,
          description: formData.description,
          report_group: formData.report_group || null,
          normal_balance: formData.normal_balance,
          is_control_account: formData.is_control_account,
          manual_posting_allowed: formData.manual_posting_allowed,
          cash_flow_category: formData.cash_flow_category || null,
        });
        toast.success('Account reporting controls updated');
      } else {
        await api.post('/finance/chart-of-accounts', {
          ...formData,
          parent_account_id: formData.parent_account_id || null,
          report_group: formData.report_group || null,
          cash_flow_category: formData.cash_flow_category || null,
          opening_balance: Number(formData.opening_balance || 0),
        });
        toast.success('Account created');
      }
      setIsModalOpen(false);
      resetForm();
      fetchPage();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save account');
    }
  };

  const handleDelete = async (account) => {
    if (account.is_system) return toast.error('System accounts cannot be deleted');
    if (!window.confirm(`Delete ${account.code} — ${account.name}?`)) return;
    try {
      await api.delete(`/finance/chart-of-accounts/${account.id}`);
      toast.success('Account deleted');
      fetchPage();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete account');
    }
  };

  const groupedAccounts = useMemo(() => accounts.reduce((result, account) => {
    const type = account.account_type;
    if (!result[type]) result[type] = [];
    result[type].push(account);
    return result;
  }, {}), [accounts]);

  const validParents = parents.filter((parent) => parent.account_type === formData.account_type);
  const validReportGroups = reportGroups[formData.account_type] || [];
  const hasAllAccountTypes = ['asset', 'liability', 'equity', 'income', 'expense'].every((type) => (groupedAccounts[type]?.length || 0) > 0);

  const formatCurrency = (amount) => new Intl.NumberFormat('en-LK', {
    style: 'currency', currency: 'LKR', minimumFractionDigits: 2,
  }).format(amount || 0);

  if (loading) return <div className="flex h-64 items-center justify-center"><div className="h-8 w-8 animate-spin rounded-full border-b-2 border-slate-900" /></div>;

  return (
    <div className="space-y-6" data-testid="chart-of-accounts-page">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Enterprise Chart of Accounts</h1>
          <p className="mt-1 text-slate-500">Account class → reporting group → header → posting account</p>
        </div>
        <div className="flex gap-2">
          {!hasAllAccountTypes && <Button onClick={initializeAccounts} variant="outline"><RefreshCw className="mr-2 h-4 w-4" />Add Missing Accounts</Button>}
          <Button onClick={openAddModal}><Plus className="mr-2 h-4 w-4" />Add Posting Account</Button>
        </div>
      </div>

      <div className="rounded-xl border border-indigo-100 bg-indigo-50 p-4 text-sm text-indigo-900">
        <div className="flex items-start gap-3">
          <ShieldCheck className="mt-0.5 h-5 w-5 shrink-0" />
          <div><strong>Enterprise safeguards:</strong> header and control accounts cannot be selected in Quick Transactions. Administrators may override a new account code only when required for an existing statutory numbering plan.</div>
        </div>
      </div>

      <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-5">
        {['asset', 'liability', 'equity', 'income', 'expense'].map((type) => {
          const Icon = accountTypeIcons[type];
          const typeAccounts = groupedAccounts[type] || [];
          const total = typeAccounts.filter((a) => !a.is_header).reduce((sum, a) => sum + Number(a.current_balance || 0), 0);
          return <div key={type} className={`rounded-lg p-4 ${accountTypeColors[type]}`}><div className="flex items-center gap-2"><Icon className="h-5 w-5" /><span className="font-medium capitalize">{type}</span></div><p className="mt-2 text-xl font-bold">{formatCurrency(Math.abs(total))}</p><p className="text-sm opacity-70">{typeAccounts.filter((a) => !a.is_header).length} posting accounts</p></div>;
        })}
      </div>

      <div className="overflow-hidden rounded-xl border border-slate-200 bg-white">
        {['asset', 'liability', 'equity', 'income', 'expense'].map((type) => {
          const Icon = accountTypeIcons[type];
          const typeAccounts = (groupedAccounts[type] || []).sort((a, b) => String(a.code).localeCompare(String(b.code)));
          return <div key={type} className="border-b border-slate-200 last:border-b-0">
            <button onClick={() => setExpandedTypes((prev) => ({ ...prev, [type]: !prev[type] }))} className={`flex w-full items-center justify-between p-4 hover:brightness-95 ${accountTypeColors[type]}`}>
              <div className="flex items-center gap-3"><Icon className="h-5 w-5" /><span className="font-semibold capitalize">{type === 'liability' ? 'Liabilities' : `${type}s`}</span><span className="text-sm opacity-70">({typeAccounts.length})</span></div>
              {expandedTypes[type] ? <ChevronDown className="h-5 w-5" /> : <ChevronRight className="h-5 w-5" />}
            </button>
            {expandedTypes[type] && typeAccounts.length > 0 && <div className="overflow-x-auto"><table className="w-full min-w-[960px]">
              <thead className="bg-slate-50 text-sm text-slate-600"><tr><th className="p-3 text-left">Code</th><th className="p-3 text-left">Account</th><th className="p-3 text-left">Reporting Group</th><th className="p-3 text-left">Control</th><th className="p-3 text-right">Balance</th><th className="p-3 text-center">Actions</th></tr></thead>
              <tbody className="divide-y divide-slate-100">{typeAccounts.map((account) => <tr key={account.id} className={account.is_header ? 'bg-slate-100/80' : 'hover:bg-slate-50'}>
                <td className={`p-3 font-mono text-sm ${account.is_header ? 'font-bold' : ''}`}>{account.code}</td>
                <td className="p-3"><div className="flex items-center gap-2">{account.is_header ? <GitBranch className="h-4 w-4 text-indigo-500" /> : <span className="ml-5 text-slate-300">└</span>}<span className={account.is_header ? 'font-semibold' : ''}>{account.name}</span>{account.is_system && <span className="rounded bg-slate-200 px-1.5 py-0.5 text-xs">System</span>}</div></td>
                <td className="p-3 text-sm text-slate-600">{account.is_header ? 'Header / grouping only' : titleCase(account.report_group || account.category)}</td>
                <td className="p-3 text-sm">{account.is_header ? <span className="rounded bg-slate-200 px-2 py-1 text-xs">No posting</span> : account.is_control_account ? <span className="rounded bg-amber-100 px-2 py-1 text-xs text-amber-800">Control account</span> : <span className="rounded bg-emerald-100 px-2 py-1 text-xs text-emerald-800">Manual posting allowed</span>}</td>
                <td className="p-3 text-right font-medium">{formatCurrency(account.current_balance || 0)}</td>
                <td className="p-3"><div className="flex justify-center gap-2"><button onClick={() => openEditModal(account)} className="rounded p-1.5 text-slate-400 hover:bg-slate-100 hover:text-slate-700"><Edit2 className="h-4 w-4" /></button>{!account.is_system && <button onClick={() => handleDelete(account)} className="rounded p-1.5 text-slate-400 hover:bg-red-50 hover:text-red-600"><Trash2 className="h-4 w-4" /></button>}</div></td>
              </tr>)}</tbody>
            </table></div>}
          </div>;
        })}
      </div>

      <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
        <DialogContent className="max-h-[92vh] overflow-y-auto sm:max-w-2xl">
          <DialogHeader><DialogTitle>{editingAccount ? 'Edit Account Controls' : 'Add New Posting Account'}</DialogTitle></DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid gap-4 sm:grid-cols-2">
              <div><label className="text-sm font-medium">Account Type</label><Select value={formData.account_type} onValueChange={(value) => setFormData((prev) => ({ ...prev, account_type: value, category: categories[value][0][0], parent_account_id: '', report_group: '', normal_balance: ['asset', 'expense'].includes(value) ? 'debit' : 'credit' }))} disabled={Boolean(editingAccount)}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent>{Object.keys(categories).map((value) => <SelectItem key={value} value={value}>{titleCase(value)}</SelectItem>)}</SelectContent></Select></div>
              <div><label className="text-sm font-medium">Parent Header</label><Select value={formData.parent_account_id || '__none__'} onValueChange={(value) => setFormData((prev) => ({ ...prev, parent_account_id: value === '__none__' ? '' : value }))} disabled={Boolean(editingAccount)}><SelectTrigger><SelectValue placeholder="Select reporting header" /></SelectTrigger><SelectContent><SelectItem value="__none__">No parent header</SelectItem>{validParents.map((parent) => <SelectItem key={parent.id} value={parent.id}>{parent.code} — {parent.name}</SelectItem>)}</SelectContent></Select><p className="mt-1 text-xs text-slate-500">Selecting a header keeps the code and reports in the correct group.</p></div>
            </div>

            <div className="grid gap-4 sm:grid-cols-2">
              <div><label className="text-sm font-medium">Account Code</label><Input value={formData.code} onChange={(e) => setFormData((prev) => ({ ...prev, code: e.target.value.replace(/\D/g, '').slice(0, 4) }))} readOnly={Boolean(editingAccount) || !formData.manual_code_override} className={!formData.manual_code_override ? 'bg-slate-50 text-slate-600' : ''} required /><p className="mt-1 text-xs text-slate-500">{formData.manual_code_override ? 'Administrator override enabled. The code must be unique.' : `Automatically generated${nextCode ? `: ${nextCode}` : ''}.`}</p></div>
              {!editingAccount && <div className="rounded-lg border border-slate-200 p-3"><label className="flex items-center gap-2 text-sm font-medium"><input type="checkbox" checked={formData.manual_code_override} disabled={!isAdmin} onChange={(e) => setFormData((prev) => ({ ...prev, manual_code_override: e.target.checked, code: e.target.checked ? '' : nextCode }))} />Administrator code override</label><p className="mt-1 text-xs text-slate-500">Use only when importing an established numbering plan.</p></div>}
            </div>

            <div><label className="text-sm font-medium">Account Name</label><Input value={formData.name} onChange={(e) => setFormData((prev) => ({ ...prev, name: e.target.value }))} required />
              {!editingAccount && accountSuggestions.length > 0 && <div className="mt-2 rounded-lg border border-amber-200 bg-amber-50 p-3"><p className="text-xs font-bold uppercase tracking-wide text-amber-800">Possible existing accounts</p><div className="mt-2 space-y-1">{accountSuggestions.map((item) => <div key={item.id} className="flex items-center justify-between gap-3 text-sm"><span><strong>{item.code}</strong> — {item.name}{item.is_header ? ' (header)' : ''}</span><span className="text-xs text-amber-700">{Math.round((item.similarity || 0) * 100)}% match</span></div>)}</div><p className="mt-2 text-xs text-amber-800">Use the existing account when it serves the same purpose. The backend blocks exact duplicates.</p></div>}
            </div>

            <div className="grid gap-4 sm:grid-cols-2">
              <div><label className="text-sm font-medium">Legal / Accounting Category</label><Select value={formData.category} onValueChange={(value) => setFormData((prev) => ({ ...prev, category: value }))} disabled={Boolean(editingAccount)}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent>{categories[formData.account_type].map(([value, label]) => <SelectItem key={value} value={value}>{label}</SelectItem>)}</SelectContent></Select></div>
              <div><label className="text-sm font-medium">Financial Statement Group</label><Select value={formData.report_group || '__auto__'} onValueChange={(value) => setFormData((prev) => ({ ...prev, report_group: value === '__auto__' ? '' : value }))}><SelectTrigger><SelectValue placeholder="Automatic from account range" /></SelectTrigger><SelectContent><SelectItem value="__auto__">Automatic from account range</SelectItem>{validReportGroups.map(([value, label]) => <SelectItem key={value} value={value}>{label}</SelectItem>)}</SelectContent></Select></div>
            </div>

            <div className="grid gap-4 sm:grid-cols-3">
              <div><label className="text-sm font-medium">Normal Balance</label><Select value={formData.normal_balance} onValueChange={(value) => setFormData((prev) => ({ ...prev, normal_balance: value }))}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent><SelectItem value="debit">Debit</SelectItem><SelectItem value="credit">Credit</SelectItem></SelectContent></Select></div>
              <div><label className="text-sm font-medium">Cash Flow Mapping</label><Select value={formData.cash_flow_category || '__none__'} onValueChange={(value) => setFormData((prev) => ({ ...prev, cash_flow_category: value === '__none__' ? '' : value }))}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent><SelectItem value="__none__">Not a cash account</SelectItem><SelectItem value="operating">Operating</SelectItem><SelectItem value="investing">Investing</SelectItem><SelectItem value="financing">Financing</SelectItem><SelectItem value="cash">Cash & equivalent</SelectItem></SelectContent></Select></div>
              <div className="rounded-lg border border-slate-200 p-3"><label className="flex items-center gap-2 text-sm font-medium"><input type="checkbox" checked={formData.is_control_account} onChange={(e) => setFormData((prev) => ({ ...prev, is_control_account: e.target.checked, manual_posting_allowed: e.target.checked ? false : prev.manual_posting_allowed }))} />Control account</label><label className="mt-2 flex items-center gap-2 text-sm"><input type="checkbox" checked={formData.manual_posting_allowed} disabled={formData.is_control_account} onChange={(e) => setFormData((prev) => ({ ...prev, manual_posting_allowed: e.target.checked }))} />Allow manual posting</label></div>
            </div>

            <div><label className="text-sm font-medium">Description</label><Input value={formData.description} onChange={(e) => setFormData((prev) => ({ ...prev, description: e.target.value }))} placeholder="Purpose and posting policy" /></div>
            {!editingAccount && <div><label className="text-sm font-medium">Opening Balance</label><Input type="number" step="0.01" value={formData.opening_balance} onChange={(e) => setFormData((prev) => ({ ...prev, opening_balance: e.target.value }))} /><p className="mt-1 text-xs text-amber-700">Prefer the controlled opening-balance workflow. This field creates an immediate journal.</p></div>}
            <DialogFooter><Button type="button" variant="outline" onClick={() => setIsModalOpen(false)}>Cancel</Button><Button type="submit">{editingAccount ? 'Update Controls' : 'Create Account'}</Button></DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
